import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  StageOne,
  StageThree,
  StageTwo,
  useMyVoiceProfile,
  useVoiceOptions,
  VoiceProgress,
} from "@/features/voice";

/**
 * The employee's voice profile.
 *
 * Three stages because the glossary makes it three, and because they ask for
 * different kinds of effort: ticking boxes, pasting old writing, and writing
 * something new. Splitting them means someone can do the easy part now and the
 * writing when they have ten minutes.
 *
 * The privacy promise sits in the header as one line rather than in a banner.
 * It has to be read before anyone pastes real writing, and a line under the
 * title gets read where a third stacked card gets skipped.
 */
export const Route = createFileRoute("/_authenticated/me/voice")({
  component: VoiceProfilePage,
});

function VoiceProfilePage() {
  const options = useVoiceOptions();
  const { data: profile, isPending, isError, refetch } = useMyVoiceProfile();
  const [stage, setStage] = useState(0);
  const [touched, setTouched] = useState(false);
  const navigate = useNavigate();

  // Open on the first unfinished stage, but only until they navigate
  // themselves. After that their choice wins.
  useEffect(() => {
    if (touched || !profile) return;
    const next = [
      profile.progress.stageOne,
      profile.progress.stageTwo,
      profile.progress.stageThree,
    ].findIndex((done) => !done);
    setStage(next === -1 ? 0 : next);
  }, [profile, touched]);

  const jump = (index: number) => {
    setTouched(true);
    setStage(index);
  };

  // Saving the last stage is the end of the job. When the other two are done
  // as well, the profile is complete and the place to be is This month,
  // where the prompts written from it arrive. Someone who jumped ahead to
  // stage three with earlier stages unfinished stays here to finish them.
  const finish = () => {
    setTouched(true);
    if (profile?.progress.stageOne && profile?.progress.stageTwo) {
      toast.success("Voice profile complete. Your prompts are written from it.");
      void navigate({ to: "/me" });
    } else {
      // Saved, but an earlier stage is still open: say so rather than sit
      // there, which read as the save having done nothing.
      toast.success("Saved. Finish the stages marked above and your prompts follow.");
    }
  };

  if (isPending || options.isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (isError || options.isError) return <ErrorState onRetry={() => void refetch()} />;

  const complete = profile?.progress.complete ?? false;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="My space"
        title="Voice profile"
        description={
          <>
            So your prompts sound like you and not like everybody else.
            <span className="mt-1.5 flex items-center gap-1.5 text-xs">
              <Lock className="size-3.5 shrink-0" aria-hidden />
              Private to you. Your manager and The Motherhood cannot open this.
            </span>
          </>
        }
        actions={
          complete ? (
            <Badge variant="success">
              <CheckCircle2 /> Complete
            </Badge>
          ) : null
        }
      />

      <div className="space-y-5">
        <VoiceProgress profile={profile ?? null} current={stage} onJump={jump} />

        {stage === 0 ? (
          <StageOne
            key={profile?.updatedAt ?? "new"}
            profile={profile ?? null}
            options={options.data}
            onDone={() => jump(1)}
          />
        ) : null}
        {stage === 1 ? (
          <StageTwo
            profile={profile ?? null}
            minSamples={options.data.minSamples}
            onDone={() => jump(2)}
          />
        ) : null}
        {stage === 2 ? (
          <StageThree
            key={profile?.updatedAt ?? "new"}
            profile={profile ?? null}
            options={options.data}
            onDone={finish}
          />
        ) : null}
      </div>
    </div>
  );
}
